import * as P from "@radix-ui/react-popover"
import { ClassValue, cn, tv } from "@utils/tw"
import { ReactNode } from "react"

type PopoverSide = "top" | "right" | "bottom" | "left"
type PopoverAlign = "start" | "center" | "end"

export type PopoverProps = {
  /**
   * An optional extra class name to apply to the popover content.
   */
  className?: ClassValue
  /**
   * The controlled open state of the popover. Must be used in conjunction with onOpenChange.
   */
  open?: boolean
  /**
   * Event handler called when the open state of the popover changes.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * The modality of the popover. When set to true, interaction with outside elements will be disabled and only popover content will be visible to screen readers.
   * @default true
   */
  modal?: boolean

  /**
   * The preferred alignment against the anchor. May change when collisions occur.
   * @default center
   */
  align?: PopoverAlign
  /**
   * The preferred side of the anchor to render against when open. Will be reversed when collisions occur and `avoidCollisions` is enabled.
   * @default bottom
   */
  side?: PopoverSide
  /**
   * The distance in pixels from the anchor/trigger
   * @default 0
   */
  offsetDistance?: number

  /**
   * Enable or disable the arrow
   * @default true
   */
  arrow?: boolean

  /**
   * Whether or not to add automatic padding to the popover content.
   * @default true
   */
  padded?: boolean
  /**
   * The content of the popover.
   */
  children: ReactNode
} & (
  | {
      /** The trigger button. Required if not `anchor` set. */
      trigger: ReactNode
    }
  | {
      /** The anchor element. Required if no `trigger` set. Combine with `open` and `onOpenChange`. */
      anchor: ReactNode
    }
)

export function Popover(props: PopoverProps) {
  const styles = popoverStyles({ padded: props.padded ?? true })

  return (
    <P.Root open={props.open} onOpenChange={props.onOpenChange} modal={props.modal ?? true}>
      {"trigger" in props ? (
        <P.Trigger asChild>{props.trigger}</P.Trigger>
      ) : "anchor" in props ? (
        <P.Anchor>{props.anchor}</P.Anchor>
      ) : null}
      <P.Portal>
        <P.Content
          radix-popover={""}
          className={cn(styles.content({ className: props.className }), "PopoverContent")}
          avoidCollisions
          collisionPadding={8}
          sideOffset={props.offsetDistance ?? 0}
          align={props.align}
          side={props.side}
        >
          {props.children}
          {/* <P.Close className={styles.close()} aria-label="Close" asChild>
              <Button shape="icon" variant="link" icon="add" />
            </P.Close> */}
          {props.arrow !== false && (
            <P.Arrow asChild>
              <div className={styles.arrow()}>
                <svg width={30} height={10} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 30">
                  <path d="M0 0l22.5 22.5L45 0z" />
                </svg>
              </div>
            </P.Arrow>
          )}
        </P.Content>
      </P.Portal>
    </P.Root>
  )
}

export const popoverStyles = tv({
  slots: {
    content: ["bg-bg text-fg shadow-popover rounded-md outline-none z-[60]", "radix-popover-animation"],
    arrowWrapper: "",
    arrow:
      "fill-bg stroke-2 stroke-fg/10 transform -translate-y-[1px] relative after:block after:h-[1px] after:w-full after:absolute after:top-0 after:left-0 after:border-b after:border-bg",
    close: "absolute top-2 right-2",
  },
  variants: {
    padded: {
      true: {
        content: "p-3",
      },
    },
  },
})
